// JobsTable — the Jobs list, on the EquaOS design system: Tabs for the status
// filter, a search Input, the design-system Table with StatusPill, and the
// delete confirmation in a Modal. Same calls as before (jobService.getJobs,
// deleteJob, offerLetterApi.uploadTemplate) with the same parameters.
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { jobService } from '../../../services/job.service';
import { offerLetterApi } from '../../../services/offerLetter.service';
import ResumeUpload from '../resume/ResumeUpload';
import { toast } from 'react-toastify';
import { Card, Table, StatusPill, Tabs, Button, Input, Modal, EmptyState, Icon, useShellLayout } from '../../../ds';

const STATUS_TONE = { Completed: 'success', Incomplete: 'warning', 'In-Progress': 'info' };
const TAB_LABEL = { 'All Jobs': 'All jobs', Completed: 'Completed', Incomplete: 'Incomplete', 'In-Progress': 'In progress' };

const JobsTable = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { mobile } = useShellLayout();
  const [jobs, setJobs] = useState([]);
  const [activeTab, setActiveTab] = useState('All Jobs');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [deletingJob, setDeletingJob] = useState(false);
  const [totalResumes, setTotalResumes] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [uploadTemplateJob, setUploadTemplateJob] = useState(null);
  const [uploadingTemplate, setUploadingTemplate] = useState(false);
  const tabs = ['All Jobs', 'Completed', 'Incomplete', 'In-Progress'];
  const ITEMS_PER_PAGE = 10;
  const fetchJobs = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const filters = {};
      if (activeTab !== 'All Jobs') {
        filters.status = activeTab;
      }
      if (searchQuery) {
        filters.search = searchQuery;
      }
      const response = await jobService.getJobs({
        ...filters,
        page: currentPage,
        limit: ITEMS_PER_PAGE
      });
      if (response.data) {
        setJobs(response.data.jobs || []);
        setTotalResumes(response.data.pagination?.total || 0);
        setTotalPages(Math.ceil((response.data.pagination?.total || 0) / ITEMS_PER_PAGE));
      }
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setError('Failed to load jobs. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchJobs();
  }, [activeTab, searchQuery, currentPage]);
  useEffect(() => {
    if (location.state?.error) {
      setError(location.state.error);
      window.history.replaceState({}, document.title);
    }
  }, [location]);
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };
  const handleRowClick = (jobId) => {
    navigate(`/jobs/${jobId}`);
  };
  const handleCreateJob = () => {
    navigate('/jobs/create');
  };
  const openDeleteModal = (job, e) => {
    e.stopPropagation();
    setJobToDelete(job);
    setDeleteModalOpen(true);
  };
  const handleTemplateUpload = async (files) => {
    if (!uploadTemplateJob) return;
    try {
      setUploadingTemplate(true);
      const formData = new FormData();
      formData.append('template', files[0]);
      formData.append('jobId', uploadTemplateJob._id);
      await offerLetterApi.uploadTemplate(uploadTemplateJob._id, formData);
      toast.success(`Template uploaded for "${uploadTemplateJob.title}"`);
      setUploadTemplateJob(null);
    } catch (err) {
      toast.error('Failed to upload template');
    } finally {
      setUploadingTemplate(false);
    }
  };
  const handleDeleteJob = async () => {
    if (!jobToDelete) return;
    setDeletingJob(true);
    try {
      await jobService.deleteJob(jobToDelete._id);
      setJobs(jobs.filter(job => job._id !== jobToDelete._id));
      setDeleteModalOpen(false);
    } catch (err) {
      setError('Failed to delete job. Please try again.');
    } finally {
      setDeletingJob(false);
      setJobToDelete(null);
    }
  };
  const firstShown = (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const rows = jobs.map((job, index) => ({ ...job, _n: firstShown + index }));

  const columns = [
    { key: '_n', label: 'No.', render: (j) => <span className="num" style={{ color: 'var(--text-muted)' }}>{j._n}</span> },
    { key: 'jobId', label: 'Job ID', render: (j) => (
      <button type="button" className="eq-link-btn num" onClick={(e) => { e.stopPropagation(); handleRowClick(j._id); }}>{j.jobId}</button>
    ) },
    { key: 'title', label: 'Job title', render: (j) => (
      <button type="button" className="eq-link-btn" style={{ color: 'var(--text-1)' }} onClick={(e) => { e.stopPropagation(); handleRowClick(j._id); }}
        aria-label={`Open ${j.title}`}>{j.title}</button>
    ) },
    { key: 'school', label: 'School', render: (j) => j.companyId?.name || 'No school assigned' },
    { key: 'status', label: 'Status', render: (j) => <StatusPill tone={STATUS_TONE[j.status] || 'neutral'}>{j.status}</StatusPill> },
    { key: 'candidates', label: 'Candidates', render: (j) => <span className="num">{j.statistics?.totalCandidates || 0}</span> },
    { key: 'template', label: 'Offer template', render: (j) => (
      <Button size="sm" variant="secondary" icon={<Icon name="file-arrow-up" />} onClick={(e) => { e.stopPropagation(); setUploadTemplateJob(j); }}>
        {j.offerLetterTemplate ? 'Update' : 'Upload'}
      </Button>
    ) },
    { key: 'delete', label: '', render: (j) => (
      <Button size="sm" variant="ghost" icon={<Icon name="trash" />} onClick={(e) => openDeleteModal(j, e)}
        aria-label={`Delete job ${j.title}`} title="Delete job" />
    ) },
  ];

  // On phones the Table stacks: title first, then the facts, then the actions.
  const byKey = Object.fromEntries(columns.map((c) => [c.key, c]));
  const phoneColumns = [
    { key: 'title', label: 'Job', render: (j) => (
      <div style={{ minWidth: 0 }}>
        {byKey.title.render(j)}
        <small className="num" style={{ display: 'block', color: 'var(--text-muted)', fontSize: 'var(--fs-caption)' }}>{j.jobId}</small>
      </div>
    ) },
    byKey.status,
    byKey.candidates,
    { key: 'actions', label: '', render: (j) => (
      <div style={{ display: 'flex', gap: 4 }}>{byKey.template.render(j)}{byKey.delete.render(j)}</div>
    ) },
  ];

  // Page numbers: first, last, and the ones around the current page.
  const pageItems = [];
  for (let p = 1; p <= totalPages; p += 1) {
    if (p === 1 || p === totalPages || (p >= currentPage - 1 && p <= currentPage + 1)) pageItems.push(p);
    else if (p === currentPage - 2 || p === currentPage + 2) pageItems.push(`gap-${p}`);
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: mobile ? 'var(--fs-display-md)' : 'var(--fs-display-lg)' }}>Jobs</h1>
          <p style={{ color: 'var(--text-2)', marginTop: 4 }}>
            {isLoading ? 'Loading…' : `${totalResumes} job${totalResumes === 1 ? '' : 's'}`}
          </p>
        </div>
        {/* In the page's own header, as the other EquaOS apps put a screen's actions (29 Oct 2026). */}
        <Button icon={<Icon name="plus" />} onClick={handleCreateJob}>Create new JD</Button>
      </div>

      <div className="eq-scroll-x">
        <div>
          <Tabs items={tabs.map((t) => ({ value: t, label: TAB_LABEL[t] || t }))} value={activeTab} onChange={handleTabChange} />
        </div>
      </div>

      <div style={{ maxWidth: 520 }}>
        <Input id="jobs-search" placeholder="Search by job ID or title" value={searchQuery} onChange={handleSearch}
          icon={<Icon name="magnifying-glass" style={{ color: 'var(--text-muted)' }} />} />
      </div>

      {error && (
        <div role="alert" style={{ padding: '12px 16px', borderRadius: 'var(--radius-md)', fontSize: 'var(--fs-body-sm)', color: 'var(--status-danger-text)', background: 'var(--status-danger-bg)' }}>{error}</div>
      )}

      <Card padding="0">
        {isLoading ? (
          <EmptyState icon={<Icon name="spinner" size={36} spin />} title="Loading jobs" />
        ) : jobs.length === 0 ? (
          <EmptyState icon={<Icon name="briefcase" size={36} />} title="No jobs found"
            description={searchQuery ? 'No job matches that search.' : 'Create a JD to start hiring for a role.'}
            action={searchQuery ? null : <div><Button icon={<Icon name="plus" />} onClick={handleCreateJob}>Create new JD</Button></div>} />
        ) : (
          <Table stacked={mobile} columns={mobile ? phoneColumns : columns} rows={rows} getRowId={(j) => j._id}
            onRowClick={(j) => handleRowClick(j._id)} />
        )}
      </Card>

      {!isLoading && jobs.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 'var(--fs-body-sm)', color: 'var(--text-2)' }}>
            Showing <span className="num">{firstShown}</span> to <span className="num">{Math.min(currentPage * ITEMS_PER_PAGE, totalResumes)}</span> of <span className="num">{totalResumes}</span>
          </span>
          <nav aria-label="Pages" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Button size="sm" variant="secondary" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1}>Previous</Button>
            {pageItems.map((p) => (typeof p === 'number' ? (
              <Button key={p} size="sm" variant={currentPage === p ? 'primary' : 'ghost'} aria-current={currentPage === p ? 'page' : undefined}
                onClick={() => setCurrentPage(p)}>{p}</Button>
            ) : <span key={p} style={{ color: 'var(--text-muted)' }}>…</span>))}
            <Button size="sm" variant="secondary" onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages}>Next</Button>
          </nav>
        </div>
      )}

      {uploadTemplateJob && (
        <ResumeUpload
          isOpen={!!uploadTemplateJob}
          onClose={() => setUploadTemplateJob(null)}
          onUpload={handleTemplateUpload}
          title="offerLetter"
          accept=".doc,.docx,.pdf"
          maxFiles={1}
          loading={uploadingTemplate}
        />
      )}

      <Modal
        open={deleteModalOpen}
        title="Delete job"
        onClose={() => !deletingJob && setDeleteModalOpen(false)}
        footer={(
          <>
            <Button variant="secondary" onClick={() => setDeleteModalOpen(false)} disabled={deletingJob}>Cancel</Button>
            <Button variant="danger" onClick={handleDeleteJob} loading={deletingJob}>{deletingJob ? 'Deleting…' : 'Delete'}</Button>
          </>
        )}
      >
        <p style={{ color: 'var(--text-2)' }}>
          Delete the job “{jobToDelete?.title}”? This cannot be undone.
        </p>
      </Modal>
    </>
  );
};
export default JobsTable;
